import {
  DateRangePickerValue,
  InputDateRangePicker,
} from '@dixa/lib-shared-habla';
import { ampli } from 'lib-shared-tracking';
import {
  SearchFilterConditionDate,
  SearchFilterDateRangePresetKey,
  searchFilterDateRangePresetKeys,
  SearchFilterValue,
} from 'lib-shared-types-api';
import moment, { Moment } from 'moment-timezone';
import { useCallback, useMemo } from 'react';

import { dateRangePresets } from '../../../../../../../utils/definitions';
import { ConditionFieldProps } from '../../../conditionField';
import { maxDate, timePickerProps } from '../utils/datepickerProps';

/** Represents a date range filter */
export const ConditionFieldDateRange = ({
  condition,
  disabled,
  onChange,
}: ConditionFieldProps<SearchFilterConditionDate>): JSX.Element => {
  /** Checks whether the selected value is a preset. */
  const isPresetValue = ({ value }: SearchFilterValue): boolean => {
    return value
      ? searchFilterDateRangePresetKeys.includes(
          value as SearchFilterDateRangePresetKey,
        )
      : false;
  };

  /** The date range value passed down to the datepicker component. */
  const value = useMemo((): DateRangePickerValue => {
    let startDate: Moment | null = null;
    let endDate: Moment | null = null;

    if (condition.values.length) {
      const [startValue, endValue] = condition.values;

      // If it's a preset, lookup the actual start and end dates in the preset configuration.
      if (isPresetValue(startValue)) {
        const presetKey = startValue.value;
        const presetData = dateRangePresets.find(
          ({ key }) => key === presetKey,
        );

        startDate = presetData?.startDate || moment();
        endDate = presetData?.endDate || moment();
      } else {
        startDate = startValue ? moment(startValue.value) : null;
        endDate = endValue ? moment(endValue.value) : null;
      }
    }

    return {
      startDate: startDate?.isValid() ? startDate : null,
      endDate: endDate?.isValid() ? endDate : null,
    };
  }, [condition]);

  /** Updates the condition with the new date range value. */
  const handleChange = useCallback(
    ({ startDate, endDate, presetKey }: DateRangePickerValue) => {
      // If it's a preset, the value will be the preset key and not the raw dates.
      if (presetKey) {
        const label =
          dateRangePresets.find(({ key }) => key === presetKey)?.label ?? '';
        const value: SearchFilterValue = { label, value: presetKey || '' };

        onChange([value]);

        ampli.searchFilterValueAdded({
          type: condition.field,
          operator: condition.operator,
          value: presetKey,
        });
      } else {
        const startDateValue: SearchFilterValue = {
          label: startDate?.format('L LT') ?? '',
          value: startDate?.toISOString() ?? '',
        };
        const endDateValue: SearchFilterValue = {
          label: endDate?.format('L LT') ?? '',
          value: endDate?.toISOString() ?? '',
        };

        onChange([startDateValue, endDateValue]);

        const hasCustomStartTime = startDate
          ? startDate.format('HH:mm') !== '00:00'
          : false;
        const hasCustomEndTime = endDate
          ? endDate.format('HH:mm') !== '23:59'
          : false;
        const hasCustomTime = hasCustomStartTime || hasCustomEndTime;

        ampli.searchFilterValueAdded({
          type: condition.field,
          operator: condition.operator,
          value: hasCustomTime ? 'dateWithCustomTime' : 'dateWithDefaultTime',
        });
      }
    },
    [condition, onChange],
  );

  return (
    <InputDateRangePicker
      value={value}
      disabled={disabled}
      maxDate={maxDate}
      dateRangePickerProps={{
        inputsPlacement: 'top',
        withDateInput: true,
        withTimePicker: true,
        timePickerProps: {
          start: timePickerProps,
          end: timePickerProps,
        },
        presets: dateRangePresets,
      }}
      dropdownProps={{
        fixed: true,
      }}
      onChange={handleChange}
    />
  );
};
